import { useForm } from "@tanstack/react-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import rg4js from "raygun4js";
import { useEffect, useState } from "react";
import { Form, Spinner } from "react-bootstrap";
import {
  Check,
  CheckCircleFill,
  ExclamationTriangleFill,
} from "react-bootstrap-icons";
import { useParams } from "react-router-dom";
import type { CaseFilePage, PurgatorySummary } from "@/briefpoint-client";
import {
  type CaseFileLite,
  CaseFileType,
  DocumentStatus,
} from "@/briefpoint-client";
import { useCaseFileApi } from "@/hooks/useApi";
import {
  getCaseFileLatestBatchOptions,
  getCaseFilesOptions,
  getPurgatorySummaryOptions,
  getReadUrlOptions,
} from "@/lib/query-options";
import { normalizeError } from "@/lib/utils";
import { pluralize } from "@/utils";
import Button from "../Button";
import FullScreenModal from "../FullScreenModal";
import Loading from "../Loading";
import Pagination from "../Pagination/Pagination";
import PdfViewer from "../PdfViewer";
import { toast } from "../ui/toast";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import styles from "./PurgatoryModal.module.scss";

interface Props {
  caseTitle: string;
  onClose: () => void;
}

interface FormData {
  [key: string]: boolean;
}

const PurgatoryModal = (props: Props) => {
  const queryClient = useQueryClient();
  const caseFileApi = useCaseFileApi();
  const { id: caseId } = useParams<{ id: string }>();
  const [updatingFileId, setUpdatingFileId] = useState<string | null>(null);
  const [docPdfId, setDocPdfId] = useState<string | undefined>(undefined);
  const [pageNumber, setPageNumber] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);

  const caseFilesQueryOptions = getCaseFilesOptions({
    caseFileApi,
    caseId,
    fileType: CaseFileType.Produceable,
    status: DocumentStatus.Uploaded,
    pageSize: 20,
    page: pageNumber,
    checkForResponsiveFiles: true,
  });
  const caseFilesQuery = useQuery(caseFilesQueryOptions);

  // Count of all not-withheld/not-privileged uploaded files
  const purgatorySummaryQueryOptions = getPurgatorySummaryOptions(
    caseFileApi,
    caseId,
  );
  const purgatorySummaryQuery = useQuery(purgatorySummaryQueryOptions);

  const readUrlQueryOptions = getReadUrlOptions(
    docPdfId || "",
    caseId || "",
    caseFileApi,
  );
  const readUrlQuery = useQuery(readUrlQueryOptions);

  const toggleShowPdf = (id: string) => {
    if (docPdfId === id) {
      setDocPdfId(undefined);
      // Remove the query data from the cache
      queryClient.removeQueries({
        queryKey: readUrlQueryOptions.queryKey,
      });
    } else {
      setDocPdfId(id);
      // Set the PDF url in the cache
      queryClient.setQueryData(readUrlQueryOptions.queryKey, {
        url: readUrlQuery.data,
      });
    }
  };

  // onSettled for privileged and withheld mutations
  const onSettled = () => {
    // Invalidate the case files query to refresh the data on the Withheld tab
    queryClient.invalidateQueries({
      // Use the name and caseId parameters from the query key to match the Withheld tab query key.
      // Although for some reason passing the fileType parameter doesn't work.
      queryKey: caseFilesQueryOptions.queryKey.slice(0, 2),
    });
    // Update the document count
    queryClient.invalidateQueries({
      queryKey: purgatorySummaryQueryOptions.queryKey,
    });
    setUpdatingFileId(null);
  };

  const setPrivilegedMutation = useMutation({
    mutationFn: async ({
      fileId,
      isPrivileged,
    }: {
      fileId: string;
      isPrivileged: boolean;
    }) => {
      if (!caseId) return;
      return caseFileApi.caseFileSetPrivileged({
        fileId,
        caseId,
        body: isPrivileged,
      });
    },
    onMutate: async ({ fileId, isPrivileged }) => {
      setUpdatingFileId(fileId);
      // Cancel any outgoing refetch so that we don't overwrite any other changes
      await queryClient.cancelQueries({
        queryKey: caseFilesQueryOptions.queryKey,
      });

      // Snapshot the previous value of the query data
      const previousData = queryClient.getQueryData(
        caseFilesQueryOptions.queryKey,
      );

      // Optimistically update the query data
      queryClient.setQueryData(
        caseFilesQueryOptions.queryKey,
        (oldData: CaseFilePage | undefined) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            items: oldData.items?.map((file: CaseFileLite) =>
              file.id === fileId ? { ...file, isPrivileged } : file,
            ),
          };
        },
      );
      return { previousData, fileId, isPrivileged };
    },
    // If there was an error, revert the optimistic update
    onError: (error, __, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          caseFilesQueryOptions.queryKey,
          context.previousData,
        );
      }
      rg4js("send", {
        error: normalizeError(error),
      });
      toast.error(
        "There was an error updating the privileged status. Please try again later.",
      );
    },
    onSettled,
  });

  // Mutation to set withheld status for individual files
  const setWithheldMutation = useMutation({
    mutationFn: async ({
      fileId,
      isWithheld,
    }: {
      fileId: string;
      isWithheld: boolean;
    }) => {
      if (!caseId) return;
      await caseFileApi.caseFileSetWithheld({
        fileId,
        caseId,
        body: isWithheld,
      });
    },
    onMutate: async ({ fileId, isWithheld }) => {
      setUpdatingFileId(fileId);
      // Cancel any outgoing refetch so that we don't overwrite any other changes
      await Promise.all([
        queryClient.cancelQueries({
          queryKey: caseFilesQueryOptions.queryKey,
        }),
        queryClient.cancelQueries({
          queryKey: purgatorySummaryQueryOptions.queryKey,
        }),
      ]);

      // Snapshot the previous value of the query data
      const previousCaseFilesQueryData = queryClient.getQueryData(
        caseFilesQueryOptions.queryKey,
      );

      const previousPurgatorySummaryQueryData = queryClient.getQueryData(
        purgatorySummaryQueryOptions.queryKey,
      );

      // Optimistically update query data
      queryClient.setQueryData(
        caseFilesQueryOptions.queryKey,
        (oldData: CaseFilePage | undefined) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            items: oldData.items?.map((file: CaseFileLite) =>
              file.id === fileId ? { ...file, isWithheld } : file,
            ),
          };
        },
      );

      // Optimistically update purgatory summary query data
      queryClient.setQueryData(
        purgatorySummaryQueryOptions.queryKey,
        (oldData: PurgatorySummary | undefined) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            fileCount: oldData.fileCount + (isWithheld ? -1 : 1),
          };
        },
      );

      return {
        previousCaseFilesQueryData,
        previousPurgatorySummaryQueryData,
        fileId,
        isWithheld,
      };
    },
    onError: (error, __, context) => {
      // If there was an error, revert the optimistic update
      if (context?.previousCaseFilesQueryData) {
        queryClient.setQueryData(
          caseFilesQueryOptions.queryKey,
          context.previousCaseFilesQueryData,
        );
      }
      if (context?.previousPurgatorySummaryQueryData) {
        queryClient.setQueryData(
          purgatorySummaryQueryOptions.queryKey,
          context.previousPurgatorySummaryQueryData,
        );
      }
      rg4js("send", {
        error: normalizeError(error),
      });
      toast.error(
        "There was an error updating the withheld status. Please try again later.",
      );
    },
    onSettled,
  });

  const batchQueryOptions = getCaseFileLatestBatchOptions(caseFileApi, caseId);

  const createCaseFileBatchMutation = useMutation({
    mutationFn: async () => {
      if (!caseId) return;
      return caseFileApi.caseFileCreateCaseFileBatch({
        caseId,
      });
    },
    onMutate: () => {
      // Clear the latest batch query so that it can be re-fetched after the new batch is created.
      queryClient.removeQueries({
        queryKey: batchQueryOptions.queryKey,
      });
      // Once we start processing, disable buttons until the modal unmounts.
      setIsProcessing(true);
    },
    onError: (error) => {
      rg4js("send", {
        error: normalizeError(error),
      });
      toast.error(
        "There was an error creating your batch. Please try again later.",
      );
    },
  });

  // After the batch is created, get the latest batch to trigger polling in CaseProduction.
  const batchQuery = useQuery({
    ...batchQueryOptions,
    enabled: !!caseId && createCaseFileBatchMutation.isSuccess,
  });

  // Close the modal after the batch is created and the latest batch is fetched.
  useEffect(() => {
    if (isProcessing && batchQuery.isSuccess) {
      // Reset the mutation so that it can be used again.
      createCaseFileBatchMutation.reset();
      // Close the modal.
      props.onClose();
    }
  }, [
    props.onClose,
    createCaseFileBatchMutation,
    isProcessing,
    batchQuery.isSuccess,
  ]);

  const form = useForm({
    defaultValues: {
      checkedItems:
        caseFilesQuery.data?.items?.reduce((acc, item) => {
          acc[item.id] = !item.isWithheld || false;
          return acc;
        }, {} as FormData) || {},
    },
    onSubmit: async () => {
      // Handle form submission if needed
      createCaseFileBatchMutation.mutate();
    },
  });

  // Close modal when escape key is pressed for convenience
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        props.onClose();
      }
    };

    document.addEventListener("keydown", handleEscapeKey);

    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [props.onClose]);

  return (
    <FullScreenModal
      onClose={props.onClose}
      footer={
        <div className={styles.footerContainer}>
          <div className={styles.footerLeft}>
            <div className={styles.selectedItemsCount}>
              {(purgatorySummaryQuery.data?.fileCount || 0) > 0 && (
                <CheckCircleFill color="var(--primary-3)" size={20} />
              )}
              <span>{purgatorySummaryQuery.data?.fileCount || 0}</span>{" "}
              {pluralize(purgatorySummaryQuery.data?.fileCount || 0, "File")}{" "}
              Selected
            </div>
            <div className={styles.creditsContainer}>
              <div>
                <span>{purgatorySummaryQuery.data?.pageCount || 0}</span>{" "}
                {pluralize(
                  purgatorySummaryQuery.data?.pageCount || 0,
                  "page credit",
                )}{" "}
                to process
              </div>
              <div className={styles.divider} />
              <div>
                <span>Unlimited</span> page credits during Beta
              </div>
            </div>
          </div>
          <div className={styles.footerRight}>
            <Button
              variant="outline"
              disabled={isProcessing}
              onClick={() => props.onClose()}
              size="sm"
            >
              Cancel
            </Button>
            <form.Subscribe selector={(state) => [state.values]}>
              {([values]) => {
                const selectedItemsCount = Object.values(
                  values.checkedItems,
                ).filter((v) => v === true).length;
                const noItemsSelected = selectedItemsCount === 0;

                return (
                  <Button
                    data-testid="purgatory-start-processing-button"
                    variant="default"
                    size="sm"
                    disabled={isProcessing || noItemsSelected}
                    onClick={() => createCaseFileBatchMutation.mutate()}
                  >
                    {isProcessing ? (
                      <>
                        <Spinner size="sm" />
                        &nbsp;Processing...
                      </>
                    ) : (
                      "Start Processing"
                    )}
                  </Button>
                );
              }}
            </form.Subscribe>
          </div>
        </div>
      }
    >
      <div className={styles.body}>
        <div
          className={clsx(
            styles.leftPanel,
            docPdfId ? styles.withPdf : styles.withoutPdf,
          )}
        >
          <h2 data-testid="purgatory-case-title" className={styles.caseTitle}>
            {props.caseTitle}
          </h2>
          <h1
            data-testid="purgatory-review-documents-title"
            className={styles.title}
          >
            Review Documents to Produce
          </h1>
          <div data-testid="purgatory-warning" className={styles.alert}>
            <ExclamationTriangleFill className={styles.textWarning} />
            <strong>Please note:</strong> Briefpoint does not review for
            privilege.
          </div>
          <Loading isLoading={caseFilesQuery.isLoading}>
            <div>
              <ul className={styles.documentList}>
                {caseFilesQuery.data?.items &&
                caseFilesQuery.data?.items.length > 0 ? (
                  caseFilesQuery.data?.items.map((item) => (
                    <li
                      data-testid="purgatory-document-item"
                      key={item.id}
                      className={clsx(styles.documentItem, {
                        [styles.selected]: item.id === docPdfId,
                      })}
                    >
                      <div className={styles.documentLeft}>
                        <form.Field name={`checkedItems.${item.id}`}>
                          {(field) => (
                            <>
                              <TooltipProvider delayDuration={0}>
                                <Tooltip>
                                  <TooltipTrigger
                                    className={
                                      styles.sendToClientDisabledButton
                                    }
                                  >
                                    <Form.Check
                                      data-testid="purgatory-document-checkbox"
                                      type="checkbox"
                                      id={`checkbox-${item.id}`}
                                      checked={!item.isWithheld}
                                      className={styles.checkboxInput}
                                      disabled={item.hasResponsiveFile ?? false}
                                      style={{
                                        cursor: item.hasResponsiveFile
                                          ? "not-allowed"
                                          : "pointer",
                                      }}
                                      onChange={(e) => {
                                        const isChecked = e.target.checked;
                                        field.handleChange(isChecked);
                                        // Call the API to update the withheld status
                                        setWithheldMutation.mutate({
                                          fileId: item.id,
                                          isWithheld: !isChecked,
                                        });
                                        // Reset form to reflect current state
                                        form.reset({
                                          checkedItems:
                                            caseFilesQuery.data?.items?.reduce(
                                              (acc, file) => {
                                                acc[file.name] =
                                                  !file.isWithheld || false;
                                                return acc;
                                              },
                                              {} as FormData,
                                            ) || {},
                                        });
                                      }}
                                    />
                                  </TooltipTrigger>
                                  {item.hasResponsiveFile ||
                                    (item.uploadedByClient && (
                                      <TooltipContent side="top">
                                        {item.hasResponsiveFile
                                          ? "Document included as responsive and must be processed."
                                          : item.uploadedByClient
                                            ? "Document uploaded by client and must be processed."
                                            : undefined}
                                      </TooltipContent>
                                    ))}
                                </Tooltip>
                              </TooltipProvider>

                              <label
                                data-testid="purgatory-document-name"
                                htmlFor={`checkbox-${item.id}`}
                                className={clsx(
                                  "form-check-label",
                                  styles.label,
                                  styles.cursorPointer,
                                )}
                              >
                                {item.name}
                              </label>

                              {item.isWithheld &&
                                (item.isPrivileged ? (
                                  <div
                                    className={styles.designatedAsPrivileged}
                                  >
                                    <Check
                                      size={20}
                                      color="var(--feedback-success-dark)"
                                    />
                                    Designated as Privileged
                                    <button
                                      type="button"
                                      className={styles.undo}
                                      disabled={updatingFileId === item.id}
                                      onClick={() => {
                                        setPrivilegedMutation.mutate({
                                          fileId: item.id,
                                          isPrivileged: false,
                                        });
                                      }}
                                    >
                                      Undo
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    data-testid="purgatory-designate-privileged-button"
                                    type="button"
                                    className={styles.designateAsPrivileged}
                                    disabled={updatingFileId === item.id}
                                    onClick={() => {
                                      setPrivilegedMutation.mutate({
                                        fileId: item.id,
                                        isPrivileged: true,
                                      });
                                    }}
                                  >
                                    Designate as Privileged
                                  </button>
                                ))}
                            </>
                          )}
                        </form.Field>
                      </div>
                      <button
                        data-testid="purgatory-pdf-view-button"
                        type="button"
                        className={styles.viewButton}
                        onClick={() => toggleShowPdf(item.id)}
                      >
                        View
                      </button>
                    </li>
                  ))
                ) : (
                  <p>No items to display</p>
                )}
              </ul>

              <div
                className={clsx(
                  styles.paginationContainer,
                  docPdfId ? styles.withPdf : styles.withoutPdf,
                )}
              >
                {(caseFilesQuery.data?.items?.length || 0) > 1 && (
                  <Pagination
                    navigate={(page) => setPageNumber(page)}
                    currentPage={pageNumber}
                    totalPages={caseFilesQuery.data?.totalPages || 0}
                    // If itemsPerPage is not set for some reason, use 20 since thats the default value from the backend
                    itemsPerPage={caseFilesQuery.data?.itemsPerPage || 20}
                    totalItemCount={caseFilesQuery.data?.totalItemCount || 0}
                  />
                )}
              </div>
            </div>
          </Loading>
        </div>

        {docPdfId && readUrlQuery.data && (
          <div className={styles.rightPanel}>
            <PdfViewer
              height={650}
              width={270}
              url={readUrlQuery.data}
              onClose={() => setDocPdfId(undefined)}
            />
          </div>
        )}
      </div>
    </FullScreenModal>
  );
};

export default PurgatoryModal;
